import React, { useMemo, useState, useCallback } from 'react';
import {
  DataGrid,
  GridToolbar,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { CSVLink } from 'react-csv';
import { FileText } from 'lucide-react';
import { 
  Pagination, 
  Box, 
  TextField, 
  Typography,
  ThemeProvider,
  createTheme,
  IconButton,
  Tooltip
} from '@mui/material';

// Button component
const Button = ({ children, ...props }) => (
  <button {...props} className="px-4 py-2 bg-red-800 text-white rounded hover:bg-red-800 transition-colors">
    {children}
  </button>
);

function CustomToolbar() {
  return (
    <GridToolbarContainer className="flex justify-between p-2 bg-white">
      <div className="flex gap-2">
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </div>
    </GridToolbarContainer>
  );
}

function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Pagination
      color="primary"
      count={pageCount}
      page={page + 1}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
    />
  );
}

// Create a custom theme matching the image
const customTheme = createTheme({
  palette: {
    primary: {
      main: '#991b1b', // Dark red color from image
      light: '#b91c1c',
      dark: '#7f1d1d',
    },
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#991b1b', // Dark red for header
            color: '#ffffff',
            fontWeight: 700,
            borderBottom: 'none',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            fontSize: '0.875rem',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: '1px solid rgba(255, 255, 255, 0.2)',
          },
          '& .MuiDataGrid-columnHeaderCheckbox': {
            backgroundColor: '#991b1b',
            borderRight: 'none',
          },
          '& .MuiDataGrid-row': {
            borderBottom: 'none',
            minHeight: '36px !important',
            maxHeight: '36px !important',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f9fafb',
          },
          '& .MuiDataGrid-cell': {
            borderRight: 'none',
            borderBottom: '1px solid #e5e7eb',
            fontSize: '0.875rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4px 16px',
            height: '36px',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
          },
          '& .MuiTablePagination-root': {
            color: '#4b5563',
          },
          '& .MuiDataGrid-cellCheckbox': {
            backgroundColor: '#ffffff',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#d1d5db',
          '&.Mui-checked': {
            color: '#991b1b',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: '#991b1b',
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#991b1b',
            color: '#ffffff',
          },
        },
      },
    },
  },
});

const AdvanceMaterialTable = ({ 
  data = [], 
  defaultVisibleColumns = [], 
  onSelectedRowsChange = () => {}, 
  publicationId 
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectionModel, setSelectionModel] = useState([]);
  const [pageSize, setPageSize] = useState(15);

  // Initialize column visibility model
  const initialVisibilityModel = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return {};
    const allColumns = Object.keys(data[0]);
    return allColumns.reduce((acc, column) => {
      acc[column] = defaultVisibleColumns.length === 0 || defaultVisibleColumns.includes(column);
      return acc;
    }, {});
  }, [data, defaultVisibleColumns]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(initialVisibilityModel);

  // Add index to data
  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((item, index) => ({
      ...item,
      id: index,
      SN: index + 1
    }));
  }, [data]);

  // Get selected rows data for export
  const getSelectedRowsData = useCallback(() => {
    if (!selectionModel.length) return rows;
    const selectedData = selectionModel.map(id => {
      const row = rows.find(r => r.id === id);
      return row ? row : null;
    }).filter(Boolean);
    return selectedData;
  }, [selectionModel, rows]);

  // Column Definitions
  const columns = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    const firstRow = data[0];
    if (!firstRow) return [];

    // Create SN column
    const snColumn = {
      field: 'SN',
      headerName: 'SN',
      width: 70,
      sortable: false,
      filterable: false,
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'center',
      hide: false,
      renderHeader: (params) => {
        // Convert header name to match the format in Image 2 (lowercase first letter)
        const headerName = params.colDef.headerName.charAt(0).toLowerCase() + params.colDef.headerName.slice(1);
        
        // Split long headers with special handling for columns like "onset potential"
        if (headerName.includes('onset potential')) {
          const parts = headerName.split('(');
          return (
            <div className="flex flex-col justify-center items-center text-center w-full h-full">
              <div>{parts[0].trim()}</div>
              <div>({parts[1]}</div>
            </div>
          );
        }
        
        return (
          <div className="flex justify-center items-center text-center w-full h-full">
            {headerName}
          </div>
        );
      },
    };

    // Create other columns
    const dataColumns = Object.keys(firstRow).map(key => ({
      field: key,
      headerName: key.charAt(0).toUpperCase() + 
                 key.slice(1).replace(/_/g, ' ')
                    .replace(/\(\+\-0\.1_mA\/cm2\)/g, "(±0.1 mA/cm²)"),
      flex: 1,
      minWidth: 150,
      sortable: true,
      filterable: true,
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      align: 'center',
      
      // Special rendering for DOI column
      ...(key === 'DOI' && {
        minWidth: 300,
        width: 320,
        renderCell: (params) => {
          if (!params.value) return '';
          return (
            <div className="w-full flex justify-center items-center">
              <a
                href={`https://dx.doi.org/${params.value}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs font-medium rounded-md overflow-hidden"
              >
                <span className="bg-gray-800 text-white px-2 py-1">DOI</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 hover:underline">
                  {params.value}
                </span>
              </a>
            </div>
          );
        }
      })
    }));

    return [snColumn, ...dataColumns];
  }, [data]);

  const handleSelectionModelChange = (newSelectionModel) => {
    setSelectionModel(newSelectionModel);
    onSelectedRowsChange(newSelectionModel);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
  };

  // If data is not provided or invalid, show a message
  if (!Array.isArray(data)) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="error">
          No data available or invalid data format
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <div className="w-full h-full flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <Typography variant="h6" className="font-semibold">
            {data.length} materials match your search
            {selectionModel.length > 0 && ` (${selectionModel.length} selected)`}
          </Typography>
          
          <div className="flex items-center space-x-4">
            <TextField
              value={searchText}
              onChange={handleSearchChange}
              placeholder="Search across all columns..."
              variant="outlined"
              size="small"
              className="min-w-[250px]"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                  },
                  '&:hover fieldset': {
                    borderColor: '#d1d5db',
                  },
                }
              }}
            />
            
            <Button onClick={() => {}} className="bg-red-800 hover:bg-red-900">
              <span className="mr-2">Columns & Filters</span>
            </Button>
            
            <CSVLink
              data={getSelectedRowsData()}
              filename="MaterialsData.csv"
              className="no-underline"
            >
              <Button className="bg-red-800 hover:bg-red-900">
                <FileText className="inline-block mr-2" size={16} />
                Export Table
              </Button>
            </CSVLink>
          </div>
        </div>

        <Box sx={{ 
          height: 600, 
          width: '100%',
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .super-app-theme--header': {
            backgroundColor: '#991b1b',
            color: '#ffffff',
          },
        }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 15, 25, 50]}
            checkboxSelection
            disableSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={handleSelectionModelChange}
            components={{
              Toolbar: CustomToolbar,
              Pagination: CustomPagination,
            }}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            getRowId={(row) => row.id}
            filterMode="client"
            quickFilterValues={[searchText]}
            density="compact"
            rowHeight={36}
            headerHeight={100}
            sx={{
              boxShadow: 2,
              '& .MuiDataGrid-cell:hover': {
                color: 'primary.main',
              },
              '& .MuiDataGrid-columnHeader': {
                whiteSpace: 'normal',
                lineHeight: '1.2',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: '1.2',
                overflow: 'visible',
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'lowercase',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#991b1b',
                color: '#ffffff',
              }
            }}
            sx={{
              boxShadow: 2,
              '& .MuiDataGrid-cell:hover': {
                color: 'primary.main',
              },
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'id', sort: 'asc' }],
              },
              pagination: {
                paginationModel: { page: 0, pageSize: 15 },
              },
              columns: {
                columnVisibilityModel: initialVisibilityModel,
              },
            }}
          />
        </Box>
      </div>
    </ThemeProvider>
  );
};

export default AdvanceMaterialTable;